/**
 * Smart feedback utilities — encode/decode structured review state in the feedback column.
 *
 * Smart feedback is stored as a JSON string with shape:
 * { type: "smart", text: "do: blah", items: ["v:do","s1"], selected: ["2:4"], wordFeedback: { do: "blah" } }
 *
 * Plain feedback remains a raw string. These helpers transparently handle both.
 */

import { cleanWord } from '../data/wordSplitter';
import { canonicalSentenceId } from './itemTypes';

const SMART_PREFIX = '{"type":"smart"';

/** Fast check: does this feedback string look like smart review JSON? */
export function isSmartFeedback(feedback) {
  return typeof feedback === 'string' && feedback.startsWith(SMART_PREFIX);
}

/** Parse smart feedback JSON. Returns the parsed object or null. */
export function parseSmartFeedback(feedback) {
  if (!isSmartFeedback(feedback)) return null;
  try {
    const parsed = JSON.parse(feedback);
    return parsed && parsed.type === 'smart' ? parsed : null;
  } catch {
    return null;
  }
}

/** Extract human-readable text from feedback (smart JSON or plain string). */
export function getDisplayFeedback(feedback) {
  if (!feedback) return '';
  const parsed = parseSmartFeedback(feedback);
  return parsed ? (parsed.text || '') : feedback;
}

/** Extract feedback text relevant to a specific item from smart feedback. */
export function getItemFeedback(feedback, itemId, itemText) {
  const parsed = parseSmartFeedback(feedback);
  if (!parsed) return null;
  // Check if this item was actually failed in the smart review
  // Canonical compare: parsed.items comes from the DB, itemId from the lesson
  // config / getProjectSentences — different producers, so the spellings can differ.
  if (!parsed.items?.map(canonicalSentenceId).includes(canonicalSentenceId(itemId))) return null;
  // Try to find word-specific feedback
  const wordKey = itemId.startsWith('v:')
    ? itemId.slice(2)
    : itemText ? cleanWord(itemText) : undefined;
  const wordFeedback = wordKey && parsed.wordFeedback?.[wordKey];
  return wordFeedback || parsed.text || '';
}

/** Extract per-word audio URL for a specific item from smart feedback. */
export function getItemAudioUrl(feedback, itemId) {
  const parsed = parseSmartFeedback(feedback);
  if (!parsed?.wordAudio || !parsed.items?.map(canonicalSentenceId).includes(canonicalSentenceId(itemId))) return null;
  const wordKey = itemId.startsWith('v:') ? itemId.slice(2) : null;
  return (wordKey && parsed.wordAudio[wordKey]) || null;
}

/** Build a smart feedback JSON string from structured data. */
export function buildSmartFeedback({ text, items, selected, wordFeedback, wordAudio, wordActions, extras }) {
  const obj = {
    type: 'smart',
    text: text || '',
    items,
    selected,
    wordFeedback,
  };
  // Only include wordAudio if there are entries
  if (wordAudio && Object.keys(wordAudio).length > 0) {
    obj.wordAudio = wordAudio;
  }
  // wordActions: { "<sentIdx>:<wordIdx>": ["word" | "root"] } — which forms the teacher picked per clicked word.
  if (wordActions && Object.keys(wordActions).length > 0) {
    obj.wordActions = wordActions;
  }
  // extras: { sentences: [sentenceId,...], combined: true } — sentence-level / full-passage picks.
  if (extras && ((extras.sentences && extras.sentences.length > 0) || extras.combined)) {
    obj.extras = {};
    if (extras.sentences && extras.sentences.length > 0) obj.extras.sentences = extras.sentences;
    if (extras.combined) obj.extras.combined = true;
  }
  return JSON.stringify(obj);
}
